import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { supabase } from '../../services/supabaseClient';
import { serviceRoleClient } from '../../services/serviceRoleClient';
import { AdminService } from '../../services/adminService';
import { ENV } from '../../config/env';
import { WhatsAppAutomationService } from '../../services/whatsappAutomationService';
import {
  resolveMessageDisplayContent,
  resolveReplyPreview,
  resolveTemplateButtons,
} from '../../utils/whatsappTemplateContent';
import { TemplateBubbleButtons } from '../../components/admin/WhatsAppTemplatePreview';
import {
  Search, Send, RefreshCw, UserCheck, ChevronLeft,
  Volume2, VolumeX, MessageSquare, Users, ArrowDownCircle,
  Bot, MousePointerClick, Reply, Clock3, Filter,
} from 'lucide-react';

const CHAT_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'window_open', label: 'Chat window open', icon: Clock3 },
  { id: 'replied', label: 'Replied', icon: Reply },
  { id: 'link_click', label: 'Link click', icon: MousePointerClick },
  { id: 'ai_chat', label: 'AI chat', icon: Bot },
];

function isCurrentUserAdmin() {
  try {
    const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
    if (user?.isAdmin === true) return true;
    const roleHints = [
      user?.currentRole,
      user?.role,
      user?.primary_role,
      user?.primaryRole,
      ...(Array.isArray(user?.roles) ? user.roles : []),
      ...(Array.isArray(user?.normalizedRoles) ? user.normalizedRoles : []),
    ]
      .map((r) => String(r || '').toLowerCase())
      .filter(Boolean);
    return roleHints.some((r) => r === 'admin' || r.includes('admin'));
  } catch {
    return false;
  }
}

/** Full number for admin; last-4 mask for everyone else */
function displayContactPhone(p, asAdmin) {
  const raw = String(p || '').trim();
  if (!raw) return '';
  if (!asAdmin) {
    const digits = raw.replace(/\D/g, '');
    if (!digits) return '';
    return `****${digits.slice(-4)}`;
  }
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 10) return `+91 ${digits}`;
  if (digits.length === 12 && digits.startsWith('91')) return `+${digits.slice(0, 2)} ${digits.slice(2)}`;
  if (raw.startsWith('+')) return raw;
  return digits || raw;
}

/** Normalize inbox search: 91XXXXXXXXXX / +91... / 10-digit → last 10 digits */
function normalizeInboxSearch(raw) {
  const t = String(raw || '').trim();
  if (!t) return '';
  const digits = t.replace(/\D/g, '');
  const looksLikePhone = digits.length >= 7 && digits.length <= 15
    && /^[+]?[\d\s()-]+$/.test(t);
  if (looksLikePhone) {
    return digits.length >= 10 ? digits.slice(-10) : digits;
  }
  return t;
}

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  if (d.toDateString() === now.toDateString()) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const y = new Date(); y.setDate(now.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return 'Yesterday';
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

function getWhatsAppUserId() {
  try {
    const user = JSON.parse(localStorage.getItem('roadserve_current_user') || '{}');
    if (user?.id && String(user.id).length > 20) return user.id;
  } catch {
    // ignore
  }
  return ENV.WHATSAPP_SYSTEM_USER_ID || null;
}

const WA_SEND_HEADERS = () => ({
  'Content-Type': 'application/json',
  apikey: ENV.SUPABASE_ANON_KEY,
  Authorization: `Bearer ${ENV.SUPABASE_ANON_KEY}`,
});

const isHttpUrl = (url) => /^https?:\/\//i.test(String(url || ''));
const isMetaMediaRef = (url) => String(url || '').startsWith('meta-media-id:');

function ChatMedia({ message, onResolved }) {
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState('');
  const onResolvedRef = useRef(onResolved);
  const url = message?.media_url || '';
  const type = message?.message_type || 'text';
  const messageId = message?.id;

  useEffect(() => {
    onResolvedRef.current = onResolved;
  }, [onResolved]);

  useEffect(() => {
    if (!messageId || !isMetaMediaRef(url)) return undefined;
    let cancelled = false;
    setResolving(true);
    setResolveError('');
    (async () => {
      try {
        const res = await fetch(`${ENV.SUPABASE_URL}/functions/v1/whatsapp-media`, {
          method: 'POST',
          headers: WA_SEND_HEADERS(),
          body: JSON.stringify({ message_id: messageId }),
        });
        const json = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (json?.success && json.media_url) {
          onResolvedRef.current?.(messageId, json.media_url);
        } else {
          setResolveError(json?.error || 'Could not load media');
        }
      } catch (e) {
        if (!cancelled) setResolveError(e?.message || 'Could not load media');
      }
      if (!cancelled) setResolving(false);
    })();
    return () => { cancelled = true; };
  }, [messageId, url]);

  if (!url && !['image', 'video', 'audio', 'document'].includes(type)) return null;

  if (resolving || isMetaMediaRef(url)) {
    return (
      <div className="mb-1 text-xs text-gray-500 bg-gray-50 rounded-md px-2 py-2">
        {resolving ? 'Loading media…' : (resolveError || 'Media pending…')}
      </div>
    );
  }

  if (!isHttpUrl(url)) return null;

  if (type === 'image' || /\.(jpe?g|png|webp|gif)(\?|$)/i.test(url)) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="block mb-1">
        <img
          src={url}
          alt={message?.content || 'Image'}
          className="max-w-full max-h-64 rounded-md object-cover bg-gray-100"
          loading="lazy"
        />
      </a>
    );
  }

  if (type === 'video' || /\.(mp4|webm|mov)(\?|$)/i.test(url)) {
    return (
      <video src={url} controls className="max-w-full max-h-64 rounded-md mb-1 bg-black" />
    );
  }

  if (type === 'audio' || /\.(ogg|mp3|wav|m4a)(\?|$)/i.test(url)) {
    return <audio src={url} controls className="w-full max-w-[260px] mb-1" />;
  }

  if (type === 'document' || type === 'location' || isHttpUrl(url)) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-1 text-xs text-emerald-700 underline mb-1 break-all"
      >
        {type === 'location' ? 'Open location' : (message?.content || 'Open attachment')}
      </a>
    );
  }

  return null;
}

export default function WhatsApp({
  embedded = false,
  automationAccountIds = null,
  automationAccounts = [],
  defaultAccountId = null,
}) {
  const isAdmin = useMemo(() => isCurrentUserAdmin(), []);
  const [contacts, setContacts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [searchQ, setSearchQ] = useState('');
  const [msgText, setMsgText] = useState('');
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState('');
  const [userById, setUserById] = useState({});
  const [telecallers, setTelecallers] = useState([]);
  const [reassignUser, setReassignUser] = useState('');
  const [showReassign, setShowReassign] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [syncing, setSyncing] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [filterUser, setFilterUser] = useState('');
  const [chatFilter, setChatFilter] = useState('all');
  const [filterCounts, setFilterCounts] = useState({
    all: 0, window_open: 0, replied: 0, link_click: 0, ai_chat: 0,
    link_click_tracked: 0, link_click_meta: 0,
  });
  const [loadedAccounts, setLoadedAccounts] = useState([]);
  const [sendAccountId, setSendAccountId] = useState(defaultAccountId || '');
  const [inboxError, setInboxError] = useState('');
  const [pendingDraftPhones, setPendingDraftPhones] = useState(new Set());
  const messagesEndRef = useRef(null);
  const listScrollRef = useRef(null);
  const searchTimerRef = useRef(null);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const PAGE_SIZE = 40;

  const accountList = useMemo(() => {
    const fromProps = Array.isArray(automationAccounts) ? automationAccounts : [];
    let base = fromProps.length ? fromProps : loadedAccounts;
    base = base.filter((a) => a && a.is_active !== false);
    if (Array.isArray(automationAccountIds) && automationAccountIds.length) {
      const allow = new Set(automationAccountIds);
      base = base.filter((a) => allow.has(a.id));
    }
    return base;
  }, [automationAccounts, loadedAccounts, automationAccountIds]);

  const activeAccountId = useMemo(() => {
    if (sendAccountId && accountList.some((a) => a.id === sendAccountId)) return sendAccountId;
    if (defaultAccountId && accountList.some((a) => a.id === defaultAccountId)) return defaultAccountId;
    const def = accountList.find((a) => a.is_default);
    return def?.id || accountList[0]?.id || '';
  }, [sendAccountId, defaultAccountId, accountList]);

  useEffect(() => {
    if (defaultAccountId) setSendAccountId(defaultAccountId);
  }, [defaultAccountId]);

  useEffect(() => {
    if (Array.isArray(automationAccounts) && automationAccounts.length) return;
    let cancelled = false;
    (async () => {
      try {
        const rows = await WhatsAppAutomationService.listAccounts();
        if (!cancelled) setLoadedAccounts(rows || []);
      } catch (e) {
        console.error('[WhatsApp] listAccounts:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [automationAccounts]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const phones = await WhatsAppAutomationService.getPendingDraftPhones();
        if (!cancelled) setPendingDraftPhones(phones);
      } catch {
        if (!cancelled) setPendingDraftPhones(new Set());
      }
    })();
    const interval = setInterval(async () => {
      try {
        const phones = await WhatsAppAutomationService.getPendingDraftPhones();
        if (!cancelled) setPendingDraftPhones(phones);
      } catch { /* ignore */ }
    }, 60000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  useEffect(() => {
    if (!sendAccountId && activeAccountId) setSendAccountId(activeAccountId);
  }, [activeAccountId, sendAccountId]);

  const hydrateAssignees = useCallback(async (contactList) => {
    const ids = [...new Set((contactList || []).map((c) => c.assigned_to).filter(Boolean))];
    if (!ids.length) return;
    const { data } = await supabase.from('users').select('id, name, email, phone').in('id', ids);
    if (!data?.length) return;
    setUserById((prev) => {
      const next = { ...prev };
      data.forEach((u) => { next[String(u.id)] = u; });
      return next;
    });
  }, []);

  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => setDebouncedSearch(normalizeInboxSearch(searchQ)), 300);
    return () => {
      if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    };
  }, [searchQ]);

  const mapInboxRow = useCallback((row, accountId) => ({
    id: row.contact_id || `acct:${accountId}:${row.phone}`,
    phone: row.phone,
    name: row.name || null,
    last_message_at: row.last_message_at,
    unread_count: row.unread_count || 0,
    assigned_to: row.assigned_to || null,
    is_muted: !!row.is_muted,
    account_id: row.account_id || accountId,
    window_open: !!row.window_open,
    has_replied: !!row.has_replied,
    has_ai: !!row.has_ai,
    has_link_click: !!row.has_link_click,
    last_preview: row.last_preview || null,
  }), []);

  /** Server-paginated inbox — no 1000-row client cap, light DB load. */
  const loadInbox = useCallback(async ({ reset = false, offset = 0 } = {}) => {
    const accountId = activeAccountId;
    if (!accountId) {
      setContacts([]);
      setFilterCounts({
        all: 0, window_open: 0, replied: 0, link_click: 0, ai_chat: 0,
        link_click_tracked: 0, link_click_meta: 0,
      });
      setHasMore(false);
      setLoading(false);
      setInboxError('Select a WhatsApp account');
      return;
    }

    if (reset) {
      setLoading(true);
      setInboxError('');
    } else {
      setLoadingMore(true);
    }

    try {
      const assignee = filterUser || null;
      const [{ data: rows, error: listError }, { data: counts, error: countError }] = await Promise.all([
        serviceRoleClient.rpc('whatsapp_inbox_list', {
          p_account_id: accountId,
          p_filter: chatFilter || 'all',
          p_search: debouncedSearch || null,
          p_assignee: assignee,
          p_limit: PAGE_SIZE,
          p_offset: offset,
        }),
        offset === 0 || reset
          ? serviceRoleClient.rpc('whatsapp_inbox_counts', {
            p_account_id: accountId,
            p_search: debouncedSearch || null,
            p_assignee: assignee,
          })
          : Promise.resolve({ data: null, error: null }),
      ]);

      if (listError) {
        const msg = listError.message || 'Inbox load failed';
        setInboxError(
          /whatsapp_inbox_list|Could not find the function/i.test(msg)
            ? 'Run supabase/whatsapp-inbox-list.sql in Supabase SQL editor, then refresh.'
            : msg,
        );
        if (reset) setContacts([]);
        setHasMore(false);
        return;
      }

      if (countError) {
        console.warn('[WhatsApp] inbox counts:', countError.message);
      } else if (counts && typeof counts === 'object') {
        setFilterCounts({
          all: Number(counts.all || 0),
          window_open: Number(counts.window_open || 0),
          replied: Number(counts.replied || 0),
          link_click: Number(counts.link_click || 0),
          ai_chat: Number(counts.ai_chat || 0),
          link_click_tracked: Number(counts.link_click_tracked || 0),
          link_click_meta: Number(counts.link_click_meta || 0),
        });
      }

      const mapped = (rows || []).map((r) => mapInboxRow(r, accountId));
      setHasMore(mapped.length >= PAGE_SIZE);
      if (reset) {
        setContacts(mapped);
      } else if (offset === 0) {
        // Soft refresh: update first page without dropping already-loaded pages
        setContacts((prev) => {
          const firstPhones = new Set(mapped.map((c) => c.phone));
          const rest = prev.filter((c) => !firstPhones.has(c.phone));
          return [...mapped, ...rest];
        });
      } else {
        setContacts((prev) => {
          const seen = new Set(prev.map((c) => c.phone));
          const merged = [...prev];
          mapped.forEach((c) => {
            if (!seen.has(c.phone)) {
              seen.add(c.phone);
              merged.push(c);
            }
          });
          return merged;
        });
      }
      await hydrateAssignees(mapped);
      setInboxError('');
    } catch (e) {
      console.error('[WhatsApp] loadInbox:', e);
      setInboxError(e?.message || 'Inbox load failed');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [activeAccountId, chatFilter, debouncedSearch, filterUser, hydrateAssignees, mapInboxRow, PAGE_SIZE]);

  const loadMoreInbox = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    loadInbox({ reset: false, offset: contacts.length });
  }, [loading, loadingMore, hasMore, loadInbox, contacts.length]);

  useEffect(() => {
    setSelectedContact(null);
    setMessages([]);
    setSendError('');
    setContacts([]);
    setHasMore(true);
  }, [activeAccountId]);

  useEffect(() => {
    loadInbox({ reset: true, offset: 0 });
  }, [loadInbox]);

  const loadTelecallers = useCallback(async () => {
    try {
      const tcs = await AdminService.getTelecallers({ status: 'active' });
      setTelecallers(tcs);
      setUserById((prev) => {
        const next = { ...prev };
        tcs.forEach((u) => { next[String(u.id)] = u; });
        return next;
      });
    } catch (e) {
      console.error('[WhatsApp] loadTelecallers:', e);
    }
  }, []);

  useEffect(() => {
    loadTelecallers();
  }, [loadTelecallers]);

  // Soft refresh first page + counts (not full history dump)
  useEffect(() => {
    if (!activeAccountId) return undefined;
    const interval = setInterval(() => {
      loadInbox({ reset: false, offset: 0 });
    }, 20000);
    const ch = supabase
      .channel(`wa_admin_inbox_${activeAccountId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'whatsapp_messages' }, () => {
        loadInbox({ reset: false, offset: 0 });
      })
      .subscribe();
    return () => {
      clearInterval(interval);
      supabase.removeChannel(ch);
    };
  }, [activeAccountId, loadInbox]);

  // Keep loadContacts name for send/reassign refresh hooks
  const loadContacts = useCallback(() => loadInbox({ reset: true, offset: 0 }), [loadInbox]);

  const loadMessages = useCallback(async (phone) => {
    if (!phone) return;
    const digits = String(phone).replace(/\D/g, '');
    const phone10 = digits.length >= 10 ? digits.slice(-10) : digits;
    const variants = [...new Set([
      phone,
      phone10,
      phone10 ? `91${phone10}` : null,
      digits || null,
    ].filter(Boolean))];

    let q = supabase
      .from('whatsapp_messages')
      .select('*')
      .in('contact_number', variants)
      .order('created_at', { ascending: true })
      .limit(500);
    if (activeAccountId) {
      q = q.eq('account_id', activeAccountId);
    }
    let { data, error } = await q;

    // Official inbox may still have legacy null account_id rows
    if (!error && activeAccountId && (!data || data.length === 0)) {
      const retry = await supabase
        .from('whatsapp_messages')
        .select('*')
        .in('contact_number', variants)
        .is('account_id', null)
        .order('created_at', { ascending: true })
        .limit(500);
      data = retry.data;
      error = retry.error;
    }

    if (error) {
      console.error('[WhatsApp] loadMessages:', error);
      return;
    }
    setMessages(data || []);
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
  }, [activeAccountId]);

  useEffect(() => {
    if (!selectedContact) return;
    loadMessages(selectedContact.phone);
    if (selectedContact.id && !String(selectedContact.id).startsWith('acct:')) {
      let unreadQ = supabase
        .from('whatsapp_contacts')
        .update({ unread_count: 0 })
        .eq('id', selectedContact.id);
      if (activeAccountId) unreadQ = unreadQ.eq('account_id', activeAccountId);
      unreadQ.then(() => {}).catch(() => {});
    }
    const filter = activeAccountId
      ? `contact_number=eq.${selectedContact.phone}`
      : `contact_number=eq.${selectedContact.phone}`;
    const ch = supabase
      .channel(`wa_admin_msgs_${activeAccountId || 'all'}_${selectedContact.phone}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'whatsapp_messages',
        filter,
      }, () => loadMessages(selectedContact.phone))
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [selectedContact, loadMessages, activeAccountId]);

  const openChat = (c) => {
    setSelectedContact(c);
    setShowReassign(false);
    setShowTemplates(false);
    setSendError('');
  };

  const sendPayloadAccount = () => {
    if (!activeAccountId) return {};
    return { account_id: activeAccountId, sender_mode: 'specific_account' };
  };

  const sendMessage = async () => {
    if (!msgText.trim() || !selectedContact) return;
    if (!activeAccountId) {
      setSendError('Select a WhatsApp account before sending.');
      return;
    }
    const text = msgText.trim();
    const userId = getWhatsAppUserId();
    if (!userId) {
      setSendError('Logged-in user missing. Please re-login and try again.');
      return;
    }

    setSending(true);
    setSendError('');
    try {
      const res = await fetch(`${ENV.SUPABASE_URL}/functions/v1/whatsapp-send`, {
        method: 'POST',
        headers: WA_SEND_HEADERS(),
        body: JSON.stringify({
          to: selectedContact.phone,
          type: 'text',
          content: text,
          contact_name: selectedContact.name || null,
          user_id: userId,
          assigned_to: selectedContact.assigned_to || null,
          source: 'human',
          ...sendPayloadAccount(),
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) {
        setSendError(json?.error || `Send failed (${res.status})`);
        return;
      }
      setMsgText('');
      const optimistic = {
        id: `local-${Date.now()}`,
        contact_number: selectedContact.phone,
        direction: 'outbound',
        message_type: 'text',
        content: text,
        status: 'sent',
        created_at: new Date().toISOString(),
        account_id: activeAccountId,
      };
      setMessages((prev) => [...prev, optimistic]);
      setTimeout(() => loadMessages(selectedContact.phone), 400);
      loadContacts();
    } catch (e) {
      console.error(e);
      setSendError(e?.message || 'Network error while sending');
    }
    setSending(false);
  };

  const handleReassign = async () => {
    if (!reassignUser || !selectedContact) return;
    if (selectedContact.id && !String(selectedContact.id).startsWith('acct:')) {
      await supabase.from('whatsapp_contacts').update({ assigned_to: reassignUser }).eq('id', selectedContact.id);
    }
    let msgQ = supabase
      .from('whatsapp_messages')
      .update({ assigned_to: reassignUser })
      .eq('contact_number', selectedContact.phone);
    if (activeAccountId) msgQ = msgQ.eq('account_id', activeAccountId);
    await msgQ;
    setSelectedContact({ ...selectedContact, assigned_to: reassignUser });
    setShowReassign(false);
    loadContacts();
  };

  const toggleMute = async () => {
    if (!selectedContact || String(selectedContact.id).startsWith('acct:')) return;
    const muted = !selectedContact.is_muted;
    await supabase.from('whatsapp_contacts').update({ is_muted: muted }).eq('id', selectedContact.id);
    setSelectedContact({ ...selectedContact, is_muted: muted });
    loadContacts();
  };

  const loadTemplates = useCallback(async (accountId) => {
    try {
      const url = accountId
        ? `${ENV.SUPABASE_URL}/functions/v1/whatsapp-templates?account_id=${accountId}`
        : `${ENV.SUPABASE_URL}/functions/v1/whatsapp-templates`;
      const res = await fetch(url, {
        headers: {
          apikey: ENV.SUPABASE_ANON_KEY,
          Authorization: `Bearer ${ENV.SUPABASE_ANON_KEY}`,
        },
      });
      const json = await res.json();
      if (json.success) {
        setTemplates((json.templates || []).filter((t) => t.status === 'APPROVED'));
      }
    } catch (e) {
      console.error('[WhatsApp] loadTemplates:', e);
    }
  }, []);

  useEffect(() => {
    loadTemplates(activeAccountId);
  }, [loadTemplates, activeAccountId]);

  const syncTemplates = async () => {
    setSyncing(true);
    await loadTemplates(activeAccountId);
    setSyncing(false);
    setShowTemplates(true);
  };

  const templatesByName = useMemo(() => {
    const map = {};
    templates.forEach((tpl) => {
      if (tpl?.name) map[tpl.name] = tpl;
    });
    return map;
  }, [templates]);

  // Resolve "Template: name" bubbles once Meta templates are available
  useEffect(() => {
    if (!activeAccountId || !messages.length) return;
    const missing = messages.some(
      (m) => m.template_name && !templatesByName[m.template_name],
    );
    if (missing && templates.length === 0) {
      loadTemplates(activeAccountId);
    }
  }, [messages, templatesByName, templates.length, activeAccountId, loadTemplates]);

  const messageByWaId = useMemo(() => {
    const map = {};
    messages.forEach((m) => {
      if (m.wa_message_id) map[m.wa_message_id] = m;
    });
    return map;
  }, [messages]);

  const sendTemplate = async (tpl) => {
    if (!selectedContact) return;
    if (!activeAccountId) {
      setSendError('Select a WhatsApp account before sending.');
      return;
    }
    const userId = getWhatsAppUserId();
    if (!userId) {
      setSendError('Logged-in user missing. Please re-login and try again.');
      return;
    }

    setSending(true);
    setSendError('');
    try {
      const bodyParamCount = Number(tpl?.param_count || 0);
      const bodyParams = [];
      for (let i = 0; i < bodyParamCount; i++) {
        const defaultValue = i === 0 ? (selectedContact?.name || '').trim() : '';
        const value = window.prompt(`Enter BODY parameter {{${i + 1}}} for template "${tpl.name}"`, defaultValue);
        if (value === null) {
          setSending(false);
          return;
        }
        bodyParams.push(String(value));
      }

      const buttons = Array.isArray(tpl?.buttons) ? tpl.buttons : [];
      const dynamicUrlButtons = buttons
        .map((b, idx) => ({ ...b, idx }))
        .filter((b) => String(b?.type || '').toUpperCase() === 'URL' && /\{\{\d+\}\}/.test(String(b?.url || '')));
      const buttonParams = [];
      dynamicUrlButtons.forEach((btn) => {
        const needed = (String(btn.url || '').match(/\{\{\d+\}\}/g) || []).length;
        for (let i = 0; i < needed; i++) {
          const value = window.prompt(
            `Enter BUTTON URL parameter for button #${btn.idx + 1} (${btn.text || 'URL'})`,
            '',
          );
          if (value === null) throw new Error('Template sending cancelled');
          buttonParams.push(String(value));
        }
      });

      const sendPayload = {
        to: selectedContact.phone,
        type: 'template',
        template_name: tpl.name,
        template_params: bodyParams,
        template_button_params: buttonParams,
        template_language: tpl.language || 'en_US',
        content: tpl.body_text || tpl.name,
        contact_name: selectedContact.name || null,
        user_id: userId,
        assigned_to: selectedContact.assigned_to || null,
        source: 'human',
        ...sendPayloadAccount(),
      };
      if (tpl.header_type) sendPayload.template_header_type = tpl.header_type;
      if (tpl.header_image_url) sendPayload.template_header_image_url = tpl.header_image_url;

      const res = await fetch(`${ENV.SUPABASE_URL}/functions/v1/whatsapp-send`, {
        method: 'POST',
        headers: WA_SEND_HEADERS(),
        body: JSON.stringify(sendPayload),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json?.success === false) {
        setSendError(json?.error || `Template send failed (${res.status})`);
        return;
      }
      setShowTemplates(false);
      setTimeout(() => loadMessages(selectedContact.phone), 400);
      loadContacts();
    } catch (e) {
      console.error(e);
      setSendError(e?.message || 'Template send failed');
    }
    setSending(false);
  };

  const getUserName = (uid) => {
    if (!uid) return 'Unassigned';
    const u = userById[String(uid)];
    return u ? (u.name || u.email || 'Unknown') : 'Unassigned';
  };

  const assignedUsers = telecallers.length ? telecallers : [];

  // Server already applies search + chat/assignee filters
  const filtered = contacts;

  const handleListScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) {
      loadMoreInbox();
    }
  };

  const statusIcon = (s) => {
    if (s === 'read') return <span className="text-blue-500">✓✓</span>;
    if (s === 'delivered') return <span className="text-gray-400">✓✓</span>;
    if (s === 'sent') return <span className="text-gray-400">✓</span>;
    if (s === 'failed') return <span className="text-red-500">✗</span>;
    return null;
  };

  const getAccountLabel = (id) => {
    const acc = accountList.find((a) => a.id === id);
    if (!acc) return 'WhatsApp';
    const phone = acc.display_phone ? ` (${acc.display_phone})` : '';
    return `${acc.name}${phone}`;
  };

  const activeAccount = accountList.find((a) => a.id === activeAccountId);

  return (
    <div className={`${embedded ? 'h-[calc(100vh-168px)] min-h-[560px]' : 'h-[calc(100vh-80px)]'} flex bg-slate-100 rounded-2xl overflow-hidden border border-slate-200 shadow-sm`}>
      {/* Left: Contacts */}
      <div className={`${selectedContact ? 'hidden md:flex' : 'flex'} flex-col w-full ${embedded ? 'md:w-80' : 'md:w-[22rem]'} bg-white border-r border-slate-200`}>
        <div className="px-3 pt-3 pb-2 border-b border-slate-100 bg-gradient-to-b from-slate-50 to-white">
          <div className="flex items-center justify-between gap-2 mb-2.5">
            <div className="min-w-0">
              <p className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">Inbox</p>
              <h2 className="text-sm font-semibold text-slate-900 truncate">
                {activeAccountId ? getAccountLabel(activeAccountId) : 'WhatsApp'}
              </h2>
            </div>
            <button onClick={syncTemplates} disabled={syncing}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-slate-600 bg-white border border-slate-200 hover:bg-slate-50 px-2.5 py-1.5 rounded-lg transition shrink-0">
              <RefreshCw size={12} className={syncing ? 'animate-spin' : ''} />
              Sync
            </button>
          </div>

          <div className="relative mb-2">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={searchQ} onChange={e => setSearchQ(e.target.value)}
              placeholder="Search name or number..."
              className="w-full pl-8 pr-2 py-2 bg-slate-50 border border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 text-xs outline-none focus:bg-white focus:border-emerald-400 focus:ring-1 focus:ring-emerald-300" />
          </div>

          <div className="grid grid-cols-1 gap-1.5 mb-2">
            {accountList.length > 0 && (
              <select
                value={activeAccountId || ''}
                onChange={(e) => setSendAccountId(e.target.value)}
                className="w-full bg-white text-slate-700 text-xs rounded-lg px-2.5 py-2 outline-none border border-slate-200"
              >
                {accountList.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}{a.display_phone ? ` — ${a.display_phone}` : ''}{a.is_default ? ' (Default)' : ''}
                  </option>
                ))}
              </select>
            )}
            <select value={filterUser} onChange={e => setFilterUser(e.target.value)}
              className="w-full bg-white text-slate-700 text-xs rounded-lg px-2.5 py-2 outline-none border border-slate-200">
              <option value="">All assignees</option>
              <option value="__unassigned__">Unassigned</option>
              {assignedUsers.map(u => (
                <option key={u.id} value={u.id}>{u.name || u.email}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center justify-between gap-2 mb-1.5">
            <div className="flex items-center gap-1.5 text-[11px] text-slate-500 font-medium">
              <Filter size={12} /> Chat filters
            </div>
            <p className="text-[10px] text-slate-400">
              Showing {filtered.length}
              {chatFilter === 'link_click'
                ? ` / ${filterCounts.link_click_tracked ?? 0}`
                : (filterCounts[chatFilter] != null ? ` / ${filterCounts[chatFilter]}` : '')}
            </p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {CHAT_FILTERS.map((f) => {
              const active = chatFilter === f.id;
              const count = filterCounts[f.id] ?? 0;
              const Icon = f.icon;
              return (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => setChatFilter(f.id)}
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] border transition ${
                    active
                      ? 'bg-emerald-600 text-white border-emerald-600'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-emerald-300'
                  }`}
                >
                  {Icon ? <Icon size={11} /> : null}
                  {f.label}
                  <span className={`ml-0.5 ${active ? 'text-emerald-100' : 'text-slate-400'}`}>{count}</span>
                </button>
              );
            })}
          </div>
          {chatFilter === 'link_click' && Number(filterCounts.link_click_meta || 0) > Number(filterCounts.link_click_tracked || 0) ? (
            <p className="mt-2 text-[11px] text-violet-700 bg-violet-50 border border-violet-100 rounded-lg px-2 py-1.5">
              Meta URL clicks: {filterCounts.link_click_meta}. Per-chat list shows tracked short-link clicks only ({filterCounts.link_click_tracked}).
            </p>
          ) : null}
          {inboxError ? (
            <p className="mt-2 text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-2 py-1.5">
              {inboxError}
            </p>
          ) : null}
        </div>

        <div ref={listScrollRef} className="flex-1 overflow-y-auto" onScroll={handleListScroll}>
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw className="animate-spin text-emerald-500" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center text-slate-500 mt-10 text-sm px-4 space-y-2">
              <p className="font-medium text-slate-600">No chats match this filter</p>
              {activeAccount?.display_phone && (
                <p className="text-xs">Number: <span className="font-mono text-slate-700">{activeAccount.display_phone}</span></p>
              )}
            </div>
          ) : filtered.map(c => (
            <div key={`${activeAccountId || 'all'}:${c.phone}:${c.id}`} onClick={() => openChat(c)}
              className={`flex items-start gap-3 px-3.5 py-3 cursor-pointer border-b border-slate-50 hover:bg-slate-50 transition ${selectedContact?.phone === c.phone ? 'bg-emerald-50/70' : ''}`}>
              <div className="relative shrink-0">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-semibold text-sm">
                  {(c.name || '?')[0].toUpperCase()}
                </div>
                {c.window_open && (
                  <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 border-2 border-white" title="Chat window open" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-center gap-2">
                  <p className={`text-sm truncate ${c.unread_count > 0 ? 'font-semibold text-slate-900' : 'font-medium text-slate-800'}`}>
                    {c.name || 'Unknown'}
                  </p>
                  <span className={`text-[11px] shrink-0 ${c.unread_count > 0 ? 'text-emerald-600 font-semibold' : 'text-slate-400'}`}>
                    {fmtTime(c.last_message_at)}
                  </span>
                </div>
                <p className="text-[11px] text-slate-400 truncate mt-0.5" title={isAdmin ? String(c.phone || '') : undefined}>
                  {displayContactPhone(c.phone, isAdmin)} · {c.assigned_to ? getUserName(c.assigned_to) : 'Unassigned'}
                </p>
                {c.last_preview && !/^template:\s*/i.test(String(c.last_preview)) ? (
                  <p className="text-xs text-slate-500 truncate mt-0.5">{c.last_preview}</p>
                ) : null}
                <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                  {c.window_open && <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-emerald-50 text-emerald-700">Window</span>}
                  {c.has_replied && <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-sky-50 text-sky-700">Replied</span>}
                  {c.has_link_click && <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-violet-50 text-violet-700">Link</span>}
                  {c.has_ai && <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-700">AI</span>}
                  {pendingDraftPhones.has(c.phone) && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded-md bg-orange-50 text-orange-700">Draft</span>
                  )}
                  {c.is_muted && <VolumeX size={11} className="text-slate-400" />}
                  {c.unread_count > 0 && (
                    <span className="ml-auto bg-emerald-500 text-white text-[10px] font-bold min-w-[1.25rem] h-5 px-1 rounded-full flex items-center justify-center">
                      {c.unread_count}
                    </span>
                  )}
                </div>
              </div>
            </div>
          ))}
          {filtered.length > 0 && (
            <div className="px-3 py-3 text-center">
              {loadingMore ? (
                <RefreshCw size={16} className="inline animate-spin text-emerald-500" />
              ) : hasMore ? (
                <button
                  type="button"
                  onClick={loadMoreInbox}
                  className="text-xs text-emerald-700 hover:underline"
                >
                  Load more chats
                </button>
              ) : (
                <p className="text-[11px] text-slate-400">All matching chats loaded</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Right: Chat */}
      <div className={`${selectedContact ? 'flex' : 'hidden md:flex'} flex-col flex-1 bg-[#eef2f0]`}>
        {!selectedContact ? (
          <div className="flex-1 flex flex-col items-center justify-center text-slate-400 px-6">
            <div className="w-16 h-16 rounded-2xl bg-white border border-slate-200 shadow-sm flex items-center justify-center mb-4">
              <MessageSquare size={28} className="text-slate-300" />
            </div>
            <p className="text-base font-semibold text-slate-600">Select a conversation</p>
            <p className="text-sm text-center mt-1 max-w-sm">Filter by window open, replies, link clicks, or AI — then open a chat.</p>
          </div>
        ) : (
          <>
            {/* Chat Header */}
            <div className="bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
              <button onClick={() => setSelectedContact(null)} className="md:hidden p-1 text-slate-600"><ChevronLeft size={20} /></button>
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center font-semibold text-sm text-white flex-shrink-0">
                {(selectedContact.name || '?')[0].toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-slate-900 truncate">{selectedContact.name || 'Unknown'}</p>
                <p className="text-xs text-slate-500 truncate" title={isAdmin ? String(selectedContact.phone || '') : undefined}>
                  {displayContactPhone(selectedContact.phone, isAdmin)} · {getUserName(selectedContact.assigned_to)}
                  {activeAccount ? ` · ${activeAccount.name}` : ''}
                  {selectedContact.window_open ? ' · Window open' : ''}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={toggleMute} className="p-2 hover:bg-slate-100 rounded-lg transition text-slate-600" title={selectedContact.is_muted ? 'Unmute' : 'Mute'}>
                  {selectedContact.is_muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
                </button>
                <button onClick={() => { setShowReassign(!showReassign); setShowTemplates(false); }}
                  className="p-2 hover:bg-slate-100 rounded-lg transition text-slate-600" title="Reassign">
                  <UserCheck size={18} />
                </button>
                <button onClick={() => { syncTemplates(); setShowReassign(false); }}
                  className="p-2 hover:bg-slate-100 rounded-lg transition text-slate-600" title="Templates">
                  <ArrowDownCircle size={18} />
                </button>
              </div>
            </div>

            {/* Reassign Panel */}
            {showReassign && (
              <div className="bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3 flex-wrap">
                <Users size={16} className="text-gray-500" />
                <span className="text-sm text-gray-600 font-medium">Reassign to:</span>
                <select value={reassignUser} onChange={e => setReassignUser(e.target.value)}
                  className="flex-1 min-w-[200px] border border-gray-300 rounded-lg px-3 py-1.5 text-sm">
                  <option value="">Select user...</option>
                  {telecallers.map(u => (
                    <option key={u.id} value={u.id}>{u.name || u.email} ({u.phone || '-'})</option>
                  ))}
                </select>
                <button onClick={handleReassign} disabled={!reassignUser}
                  className="px-4 py-1.5 bg-emerald-600 text-white rounded-lg text-sm hover:bg-emerald-500 disabled:opacity-40 transition">
                  Assign
                </button>
                <button onClick={() => setShowReassign(false)} className="text-sm text-gray-500 hover:text-gray-700">Cancel</button>
              </div>
            )}

            {/* Template Panel */}
            {showTemplates && (
              <div className="bg-white border-b border-gray-200 px-4 py-3 max-h-48 overflow-y-auto">
                <p className="text-sm font-semibold text-gray-700 mb-2">Approved Templates ({templates.length})</p>
                {templates.length === 0 ? (
                  <p className="text-xs text-gray-400">No approved templates found</p>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {templates.map((tpl, i) => (
                      <div key={i} className="border border-gray-200 rounded-lg p-2 hover:bg-emerald-50 cursor-pointer transition"
                        onClick={() => sendTemplate(tpl)}>
                        <p className="text-sm font-medium text-gray-800">{tpl.name}</p>
                        <p className="text-xs text-gray-500 line-clamp-2">{tpl.body_text}</p>
                        <span className="text-[10px] text-emerald-600 font-medium">{tpl.language} • Click to send</span>
                      </div>
                    ))}
                  </div>
                )}
                <button onClick={() => setShowTemplates(false)} className="mt-2 text-xs text-gray-500 hover:text-gray-700">Close</button>
              </div>
            )}

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1">
              {messages.map((m) => {
                const isOut = m.direction === 'outbound';
                const textContent = resolveMessageDisplayContent(m, templatesByName);
                const hasMedia = !!(m.media_url || ['image', 'video', 'audio', 'document'].includes(m.message_type));
                const displayContent = textContent
                  || (m.message_type === 'template' && m.template_name && !templatesByName[m.template_name]
                    ? '' // wait for templates sync — never show bare template name
                    : (!hasMedia ? `[${m.message_type}]` : ''));
                const templateButtons = resolveTemplateButtons(m, templatesByName);
                const replyPreview = m.reply_to_content
                  ? resolveReplyPreview(
                    m.reply_to_content,
                    templatesByName,
                    m.reply_to_wa_message_id ? messageByWaId[m.reply_to_wa_message_id] : null,
                  )
                  : null;
                const templateHeaderUrl = m.message_type === 'template' && isHttpUrl(m.media_url)
                  ? m.media_url
                  : null;
                const isAi = String(m.source || '') === 'ai';
                return (
                  <div key={m.id} className={`flex ${isOut ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[75%] rounded-2xl shadow-sm overflow-hidden border ${
                      isOut
                        ? (isAi ? 'bg-amber-50 border-amber-100' : 'bg-[#d9fdd3] border-emerald-100')
                        : 'bg-white border-slate-100'
                    }`}>
                      <div className="px-3 pt-2">
                        {isAi && (
                          <p className="inline-flex items-center gap-1 text-[10px] font-medium text-amber-700 mb-1">
                            <Bot size={10} /> AI reply
                          </p>
                        )}
                        {replyPreview && (
                          <div className="border-l-4 border-emerald-400 bg-emerald-50 rounded px-2 py-1 mb-1 text-xs text-slate-600 line-clamp-2">
                            {replyPreview}
                          </div>
                        )}
                        {templateHeaderUrl ? (
                          <a href={templateHeaderUrl} target="_blank" rel="noreferrer" className="block mb-1">
                            <img src={templateHeaderUrl} alt="" className="max-w-full max-h-56 rounded-md object-cover" loading="lazy" />
                          </a>
                        ) : (
                          <ChatMedia
                            message={m}
                            onResolved={(id, mediaUrl) => {
                              setMessages((prev) => prev.map((row) => (
                                row.id === id ? { ...row, media_url: mediaUrl } : row
                              )));
                            }}
                          />
                        )}
                        {displayContent ? (
                          <p className="text-sm text-slate-800 whitespace-pre-wrap break-words">{displayContent}</p>
                        ) : null}
                      </div>
                      <TemplateBubbleButtons buttons={templateButtons} />
                      <div className="flex items-center justify-end gap-1 px-3 pb-2 pt-0.5">
                        <span className="text-[10px] text-slate-400">
                          {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </span>
                        {isOut && statusIcon(m.status)}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <div className="bg-white border-t border-slate-200 px-4 py-3">
              {sendError ? (
                <p className="text-xs text-red-600 mb-2">{sendError}</p>
              ) : null}
              {!selectedContact.window_open && (
                <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-2.5 py-1.5 mb-2">
                  24h chat window closed — free text may fail. Prefer an approved template.
                </p>
              )}
              <div className="flex items-center gap-3">
                <input value={msgText} onChange={e => { setMsgText(e.target.value); if (sendError) setSendError(''); }}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                  placeholder="Type a message..." className="flex-1 border border-slate-200 rounded-full px-4 py-2.5 text-sm outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-400 transition bg-slate-50 focus:bg-white" />
                <button onClick={sendMessage} disabled={sending || !msgText.trim() || !activeAccountId}
                  className="w-10 h-10 bg-emerald-600 text-white rounded-full flex items-center justify-center hover:bg-emerald-500 disabled:opacity-40 transition shadow-sm">
                  <Send size={18} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
